import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Banknote,
  CheckCircle2,
  Clock,
  FileText,
  Filter,
  Inbox,
  Landmark,
  ListChecks,
  UploadCloud,
  XCircle,
  Zap,
} from 'lucide-react';
import { usePaymentHistory } from '../../application/hooks/usePayments';
import { useTranslation } from '../../application/hooks/useTranslation';
import { formatDateTime, formatNumber } from '../../domain/formatters/date';
import { formatBDT } from '../../domain/formatters/currency';
import type { PaymentRecordDto } from '../../domain/contracts/api.contracts';
import { PaymentChannel, PaymentStatus } from '../../domain/enums/SystemEnums';
import { QueryState } from '../components/feedback/QueryState';
import { AmountDisplay } from '../components/ui/AmountDisplay';
import { Badge, type BadgeTone } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import './pages.css';
import './PaymentsHistory.css';
import { BKASH_ENABLED } from '../../config/features';
import { PayLink } from '../components/payments/PayLink';

const PAGE_SIZE = 10;
const STATUS_TONE: Record<string, BadgeTone> = {
  VERIFIED: 'success',
  PENDING: 'warning',
  REJECTED: 'danger',
};
const STATUS_KEY: Record<string, string> = {
  VERIFIED: 'payPolish.statusApproved',
  PENDING: 'payPolish.statusPending',
  REJECTED: 'payPolish.statusRejected',
};
const STATUS_ICON: Record<string, ReactNode> = {
  VERIFIED: <CheckCircle2 size={12} aria-hidden />,
  PENDING: <Clock size={12} aria-hidden />,
  REJECTED: <XCircle size={12} aria-hidden />,
};

/** Payment history: summary tiles, filters, and a responsive table (cards on phones). */
export default function PaymentsHistoryPage() {
  const { t, locale } = useTranslation();
  const [page, setPage] = useState(1);
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const history = usePaymentHistory({
    page,
    limit: PAGE_SIZE,
    channel: (channel || undefined) as PaymentChannel | undefined,
    status: (status || undefined) as PaymentStatus | undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const effectivePage = history.data?.page ?? page;
  const total = history.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const items = history.data?.items ?? [];
  const filtered = Boolean(channel || status || dateFrom || dateTo);

  const resetPage = () => setPage(1);

  const settledSum = items
    .filter((p) => p.status === PaymentStatus.VERIFIED)
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  const pendingCount = items.filter((p) => p.status === PaymentStatus.PENDING).length;

  const channelChip = (c: string) => {
    const kind = c === 'BKASH' ? 'bkash' : c === 'BANK_DEPOSIT' ? 'bank' : 'cash';
    const label = c === 'BKASH' ? t('payPolish.optBkash') : c === 'BANK_DEPOSIT' ? t('payPolish.optBank') : c === 'CASH_COUNTER' ? t('payPolish.optCash') : c;
    const icon = kind === 'bkash' ? <Zap size={13} aria-hidden /> : kind === 'bank' ? <Landmark size={13} aria-hidden /> : <Banknote size={13} aria-hidden />;
    return (
      <span className={`pay-channel-badge ${kind}`}>
        {icon}
        {label}
      </span>
    );
  };

  return (
    <div className="pay-page" data-testid="payments-page">
      <div className="page-head pay-head">
        <div>
          <h1 className="page-title">{t('payPolish.histTitle')}</h1>
          <p className="muted">{t('payPolish.histSubtitle')}</p>
        </div>
        <div className="pay-head-actions">
          <PayLink to="/payments/bank-slip/upload" className="pay-btn pay-btn-primary" data-testid="goto-upload">
            <UploadCloud size={16} aria-hidden />
            <span>{t('payPolish.uploadSlip')}</span>
          </PayLink>
          {BKASH_ENABLED && (
            <PayLink to="/payments/bkash" className="pay-btn pay-btn-bkash">
              <Zap size={15} aria-hidden />
              <span>{t('payPolish.payBkash')}</span>
            </PayLink>
          )}
        </div>
      </div>

      <div className="pay-stats-grid">
        <div className="pay-stat-card">
          <span className="pay-stat-icon-wrap emerald">
            <CheckCircle2 size={20} aria-hidden />
          </span>
          <div className="pay-stat-body">
            <span className="pay-stat-label">{t('payPolish.statApproved')}</span>
            <span className="pay-stat-val">{formatBDT(settledSum, locale)}</span>
          </div>
        </div>
        <div className="pay-stat-card">
          <span className="pay-stat-icon-wrap amber">
            <Clock size={20} aria-hidden />
          </span>
          <div className="pay-stat-body">
            <span className="pay-stat-label">{t('payPolish.statPending')}</span>
            <span className="pay-stat-val">{formatNumber(pendingCount, locale)}</span>
          </div>
        </div>
        <div className="pay-stat-card">
          <span className="pay-stat-icon-wrap gold">
            <ListChecks size={20} aria-hidden />
          </span>
          <div className="pay-stat-body">
            <span className="pay-stat-label">{t('payPolish.statRecords')}</span>
            <span className="pay-stat-val">{formatNumber(total, locale)}</span>
          </div>
        </div>
      </div>

      <div className="pay-filter-card">
        <div className="pay-filter-head">
          <span className="pay-filter-head-title">
            <Filter size={14} aria-hidden /> {t('payPolish.filters')}
          </span>
          <span className="muted pay-filter-count">
            {t('payPolish.showing', { shown: formatNumber(items.length, locale), total: formatNumber(total, locale) })}
          </span>
        </div>

        <div className="pay-filter-grid">
          <div className="field">
            <label className="field-label" htmlFor="pay-channel">
              {t('payments.filterChannel')}
            </label>
            <select
              id="pay-channel"
              className="field-input"
              value={channel}
              onChange={(e) => {
                setChannel(e.target.value);
                resetPage();
              }}
              data-testid="pay-channel"
            >
              <option value="">{t('payments.all')}</option>
              <option value="BKASH">{t('payPolish.optBkash')}</option>
              <option value="BANK_DEPOSIT">{t('payPolish.optBank')}</option>
            </select>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="pay-status">
              {t('payments.filterStatus')}
            </label>
            <select
              id="pay-status"
              className="field-input"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                resetPage();
              }}
              data-testid="pay-status"
            >
              <option value="">{t('payments.all')}</option>
              <option value="VERIFIED">{t('payPolish.statusApproved')}</option>
              <option value="PENDING">{t('payPolish.statusPending')}</option>
              <option value="REJECTED">{t('payPolish.statusRejected')}</option>
            </select>
          </div>

          <Input
            label={t('payments.from')}
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              resetPage();
            }}
            data-testid="pay-from"
          />

          <Input
            label={t('payments.to')}
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              resetPage();
            }}
            data-testid="pay-to"
          />
        </div>
      </div>

      <QueryState
        loading={history.isPending}
        error={history.error}
        onRetry={() => void history.refetch()}
        skeletonCount={5}
      />

      {history.data && total === 0 && (
        <Card>
          <div className="pay-empty" data-testid="empty-state">
            <span className="pay-empty-icon" aria-hidden>
              <Inbox size={26} />
            </span>
            <strong>{t('payPolish.emptyTitle')}</strong>
            <p className="muted">{t(filtered ? 'payPolish.emptyFiltered' : 'payPolish.emptyBody')}</p>
            {!filtered && (
              <PayLink to="/payments/bank-slip/upload" className="pay-btn pay-btn-primary">
                <UploadCloud size={16} aria-hidden />
                <span>{t('payPolish.uploadSlip')}</span>
              </PayLink>
            )}
          </div>
        </Card>
      )}

      {history.data && history.data.items.length > 0 && (
        <Card className="pay-table-card">
          <div className="table-wrap">
            <table className="data pay-table">
              <thead>
                <tr>
                  <th>{t('payPolish.colDate')}</th>
                  <th>{t('payPolish.colChannel')}</th>
                  <th>{t('payPolish.colStatus')}</th>
                  <th>{t('payPolish.colRef')}</th>
                  <th className="pay-num">{t('payPolish.colAmount')}</th>
                  <th aria-label={t('payPolish.viewInvoice')} />
                </tr>
              </thead>
              <tbody>
                {history.data.items.map((p: PaymentRecordDto) => (
                  <tr key={p.id} data-testid="payment-row">
                    <td className="pay-date" data-label={t('payPolish.colDate')}>
                      {formatDateTime(p.createdAt, locale)}
                    </td>
                    <td className="pay-cell-channel" data-label={t('payPolish.colChannel')}>
                      {channelChip(p.channel)}
                    </td>
                    <td className="pay-cell-status" data-label={t('payPolish.colStatus')}>
                      <Badge tone={STATUS_TONE[p.status] ?? 'neutral'}>
                        {STATUS_ICON[p.status]}
                        {p.status === 'PENDING' && p.channel === 'BKASH'
                          ? t('payments.pendingVerification')
                          : STATUS_KEY[p.status]
                            ? t(STATUS_KEY[p.status])
                            : p.status}
                      </Badge>
                    </td>
                    <td className="pay-cell-ref" data-label={t('payPolish.colRef')}>
                      {p.trxId ? (
                        <span className="pay-trx">
                          <span className="pay-trx-label">{t('payPolish.trxLabel')}</span>
                          <span className="pay-trx-mono">{p.trxId}</span>
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="pay-num pay-amount" data-label={t('payPolish.colAmount')}>
                      <AmountDisplay amount={p.amount} />
                    </td>
                    <td className="pay-cell-action">
                      <Link to={`/payments/${p.id}/invoice`} className="pay-invoice-link" data-testid={`invoice-${p.id}`}>
                        <FileText size={13} aria-hidden />
                        <span>{t('payPolish.viewInvoice')}</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pager pay-pager">
            <Button variant="secondary" size="sm" disabled={effectivePage <= 1} onClick={() => setPage((p) => p - 1)}>
              {t('common.prev')}
            </Button>
            <span className="muted tabular" data-testid="pay-page">
              {t('common.page')} <strong>{effectivePage}</strong> {t('common.of')} <strong>{totalPages}</strong>
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={effectivePage >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('common.next')}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
